import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LANGUAGE_ENTRIES } from '../../../utils/globals';
import { bookLanguageFlagPublicUrl } from './bookLanguageFlags';
import { BOOK_LANGUAGE_DUPLICATE_CODE_MESSAGE } from './singleTableListSupport';

export type BookLanguageDraft = {
  mode: 'create' | 'edit';
  id: string;
  name: string;
  /** Basename under `public/flags/`; empty = no flag. */
  filename: string;
  /** When false, language is hidden from public app bundles. */
  active: boolean;
};

type BookLanguageListFormProps = {
  draft: BookLanguageDraft;
  onChange: (next: BookLanguageDraft) => void;
  /** For create mode: ids already in the list (duplicate code prevention). */
  existingIds: ReadonlySet<string>;
};

type CodeFlagOption = { code: string; filename: string };

function associationByCode(code: string): CodeFlagOption | undefined {
  const row = LANGUAGE_ENTRIES.find((a) => a.code === code);
  return row ? { code: row.code, filename: row.filename } : undefined;
}

function renderCodeFlagRow(code: string, filename: string) {
  const fn = filename.trim();
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {fn ? (
        <Box
          component="img"
          src={bookLanguageFlagPublicUrl(fn)}
          alt=""
          sx={{
            width: 28,
            height: 18,
            objectFit: 'cover',
            borderRadius: 0.5,
            border: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 28,
            height: 18,
            borderRadius: 0.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            flexShrink: 0,
          }}
        />
      )}
      <Typography component="span" variant="body2">
        {code}
        {fn ? ` · ${fn}` : ''}
      </Typography>
    </Stack>
  );
}

export default function BookLanguageListForm({ draft, onChange, existingIds }: BookLanguageListFormProps) {
  const labelId = 'book-language-code-flag-select-label';
  const idTrimmed = draft.id.trim();
  const duplicateCode =
    draft.mode === 'create' && idTrimmed !== '' && existingIds.has(idTrimmed);

  const createOptions: CodeFlagOption[] = LANGUAGE_ENTRIES.map((a) => ({
    code: a.code,
    filename: a.filename,
  }));

  const editAssociation = associationByCode(idTrimmed);
  const editOption: CodeFlagOption =
    editAssociation != null && editAssociation.filename === draft.filename.trim()
      ? editAssociation
      : { code: idTrimmed, filename: draft.filename.trim() };

  const handleCombinedChange = (ev: SelectChangeEvent<string>) => {
    const code = ev.target.value;
    const entry = LANGUAGE_ENTRIES.find((a) => a.code === code);
    onChange({
      ...draft,
      id: code,
      filename: entry?.filename ?? '',
      name: code === '' ? '' : entry?.name ?? draft.name,
      active: draft.active,
    });
  };

  const selectValue = draft.mode === 'create' ? draft.id : idTrimmed;

  return (
    <>
      <FormControl fullWidth margin="none" required={draft.mode === 'create'} error={duplicateCode}>
        <InputLabel id={labelId} shrink={draft.mode === 'create'}>
          Хэлний код ба туг
        </InputLabel>
        <Select
          labelId={labelId}
          id="book-language-code-flag"
          name="language_code_flag"
          value={selectValue}
          label="Хэлний код ба туг"
          onChange={handleCombinedChange}
          disabled={draft.mode === 'edit'}
          displayEmpty={draft.mode === 'create'}
          renderValue={(selected) => {
            if (draft.mode === 'create' && selected === '') {
              return (
                <Typography component="span" variant="body2" color="text.secondary">
                  Сонгоно уу
                </Typography>
              );
            }
            const code = String(selected);
            const fn =
              draft.mode === 'edit'
                ? draft.filename.trim()
                : (associationByCode(code)?.filename ?? '').trim();
            return renderCodeFlagRow(code, fn);
          }}
        >
          {draft.mode === 'create' && (
            <MenuItem value="">
              <Typography color="text.secondary">Сонгоно уу</Typography>
            </MenuItem>
          )}
          {draft.mode === 'create' &&
            createOptions.map((row) => (
              <MenuItem key={row.code} value={row.code}>
                {renderCodeFlagRow(row.code, row.filename)}
              </MenuItem>
            ))}
          {draft.mode === 'edit' && idTrimmed !== '' && (
            <MenuItem value={editOption.code}>{renderCodeFlagRow(editOption.code, editOption.filename)}</MenuItem>
          )}
        </Select>
        {duplicateCode && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
            {BOOK_LANGUAGE_DUPLICATE_CODE_MESSAGE}
          </Typography>
        )}
      </FormControl>
      <TextField
        label="Нэр"
        name="name"
        value={draft.name}
        onChange={(ev) => onChange({ ...draft, name: ev.target.value })}
        autoComplete="off"
        fullWidth
        required
        margin="none"
      />
      <FormControlLabel
        control={
          <Switch
            checked={draft.active}
            onChange={(_, checked) => onChange({ ...draft, active: checked })}
            name="active"
            color="primary"
          />
        }
        label="Идэвхтэй (нийтийн апп-д харагдана)"
      />
    </>
  );
}
